import { useCallback, useEffect, useState } from 'react';
import ICONS from './snackbarIcons';
import './Snackbar.scss';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { snackbarI18n } from './i18n';

const EXIT_DURATION_MS = 300;
const ENTER_DELAY_MS = 30;

const SnackbarItem = ({ snack, removeSnackbar }) => {
  const { t } = useScopedTranslation(snackbarI18n);
  const { id, message, type, duration } = snack;
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setIsVisible(false);
    setTimeout(() => removeSnackbar(id), EXIT_DURATION_MS);
  }, [isClosing, id, removeSnackbar]);

  useEffect(() => {
    const enterTimer = setTimeout(() => setIsVisible(true), ENTER_DELAY_MS);
    return () => clearTimeout(enterTimer);
  }, []);

  useEffect(() => {
    const dismissTimer = setTimeout(handleClose, duration);
    return () => clearTimeout(dismissTimer);
  }, [duration, handleClose]);

  return (
    <div
      className={[
        'snackbar-item',
        `snackbar--${type}`,
        isVisible ? 'is-visible' : '',
        isClosing ? 'is-closing' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role='alert'
    >
      <span className='snackbar-item__icon'>{ICONS[type] ?? ICONS.info}</span>
      <span className='snackbar-item__message'>{message}</span>
      <button
        type='button'
        className='snackbar-item__close'
        onClick={handleClose}
        aria-label={t('snackbar_close')}
      >
        <svg
          width='14'
          height='14'
          viewBox='0 0 14 14'
          fill='none'
          aria-hidden='true'
        >
          <path
            d='M2 2l10 10M12 2L2 12'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
          />
        </svg>
      </button>
    </div>
  );
};

const Snackbar = ({ snacks, removeSnackbar }) => {
  if (!snacks.length) return null;

  return (
    <div className='snackbar-container'>
      {snacks.map((snack) => (
        <SnackbarItem
          key={snack.id}
          snack={snack}
          removeSnackbar={removeSnackbar}
        />
      ))}
    </div>
  );
};

export default Snackbar;
